import { t } from "../core/i18n.js";
import { runtime } from "../core/runtime.js";
import { esc, tasksFor } from "../core/model.js";
import { icon } from "./icons.js";
import { renderSequenceSettings } from "./focus-sequence.js";
import {
  timerHidden,
  timerVisibilityButton,
  timerProgress,
} from "./floating-timer.js";

function preference(label, key, value, extra = "") {
  return `<div class="pf-pref"><label><input type="checkbox" class="pf-check" data-pref-check="${key}" ${value ? "checked" : ""}><span>${t(label)}</span></label>${extra}</div>`;
}

export function renderPomodoroPage(studyPanel, display) {
  const { pom, stats } = runtime.state;
  const f = runtime.focusTimer;
  const total =
    f?.totalBlocks || pom.blocks.reduce((sum, b) => sum + b.repeats, 0);
  const rest = f?.mode === "rest";
  const status = f?.finished
    ? "COMPLETE"
    : f?.paused
      ? "Paused"
      : f?.running
        ? "In progress"
        : "Ready to focus";
  const tasks = tasksFor(runtime.selectedDate);
  const selected = pom.selectedTask || "";
  const options = [
    `<option value="">${t("No selected task")}</option>`,
    ...tasks.map(
      (task) =>
        `<option value="${esc(task.name)}" ${task.name === selected ? "selected" : ""}>${esc(task.name)}</option>`,
    ),
  ];
  // Keep a chosen task visible even when it is not in today's plan.
  if (selected && !tasks.some((task) => task.name === selected))
    options.push(
      `<option value="${esc(selected)}" selected>${esc(selected)}</option>`,
    );
  const playLabel = f?.running ? "PAUSE" : f?.paused ? "RESUME" : "START";
  const minutes = Math.round(stats.today.minutes || 0);

  return `<div class="kun-dash pf-page">
    <section class="kun-sheet" aria-labelledby="pfTitle">
      <header class="kun-heading"><h1 id="pfTitle">${t("Focus timer")}</h1><div class="kun-heading-meta"><span>${t("Block {current} of {total}", { current: f?.block || 1, total })}</span><button type="button" class="kun-link" data-floating-timer>${icon("popout")}${t("Floating timer")}</button></div></header>
      <div class="pf-stage${f?.running ? " running" : ""}${rest ? " resting" : ""}">
        <div class="pf-ring"><svg viewBox="0 0 320 320" aria-hidden="true"><circle class="pf-ring-track" cx="160" cy="160" r="150"/><circle class="pf-ring-value" data-focus-progress cx="160" cy="160" r="150" pathLength="100" stroke-dasharray="${timerProgress()} 100"/></svg>
          <div class="pf-center"><span class="pf-phase">${t(rest ? "REST TIME" : "FOCUS TIME")}</span><div class="pf-time" id="mainTimer" role="timer" aria-label="${t("Focus timer")}" ${timerHidden() ? "hidden" : ""}>${display}</div><div id="pomodoroArtwork" class="pf-time-hidden" ${timerHidden() ? "" : "hidden"}>${t("Time is hidden")}</div><span class="pf-status"><i aria-hidden="true"></i>${t(status)}</span></div>
        </div>
        <label class="pf-task"><span>${t("Focus task")}</span><select data-focus-task aria-label="${t("Focus task")}">${options.join("")}</select></label>
        <div class="pf-controls"><button type="button" class="pf-icon" data-pom="reset" aria-label="${t("RESET")}" title="${t("RESET")}" ${f ? "" : "disabled"}>${icon("reset")}</button><button type="button" class="kun-primary pf-main" data-pom="${f?.running ? "pause" : "start"}">${icon(f?.running ? "pause" : "play")}${t(playLabel)}</button><button type="button" class="pf-icon" data-pom="skip" aria-label="${t("Skip to next phase")}" title="${t("Skip to next phase")}" ${f && !f.finished ? "" : "disabled"}>${icon("arrow")}</button></div>
        <div class="pf-foot">${timerVisibilityButton()}<span class="pf-shortcut"><kbd>Space</kbd>${t("pause / resume")}</span></div>
      </div>
      ${studyPanel}
    </section>
    <aside class="kun-analytics pf-side" aria-label="${t("Session settings")}">
      <section class="pf-today" aria-labelledby="pfTodayTitle"><div class="kun-section-head"><h2 id="pfTodayTitle">${t("Focused today")}</h2></div><div class="pf-today-value"><strong>${minutes}</strong><span>${t("min")}</span></div><p>${t("{count} focus sessions", { count: stats.today.sessions || 0 })}</p></section>
      ${renderSequenceSettings()}
      <section class="pf-prefs" aria-labelledby="pfPrefsTitle"><div class="kun-section-head"><h2 id="pfPrefsTitle">${t("Sound and alerts")}</h2></div>${preference("Completion Sound", "sound", pom.sound, `<button type="button" class="kun-link" data-preview-chime>${icon("play")}${t("Listen")}</button>`)}${preference("Notifications", "notifications", pom.notifications)}</section>
    </aside>
  </div>`;
}
